import React, { useCallback, useEffect, useState } from 'react';
import {
  DeleteConfirmation,
  FiltersBar,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  Page,
  SearchInput,
  SingleSelectFilter,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import ReportScheduleForm from './ReportScheduleForm';

export default function ReportSchedulesPage() {
  const documentTitle = useDocumentTitle('Report Schedules');

  const { workspace } = useWorkspace();
  const api = useApi();
  const [{ data, isReady, isFetching }, setQuery] = useState({ data: null, isReady: false, isFetching: false });
  const toast = useToast();
  const confirmation = useConfirmation();

  const [params, setParams] = useState({
    q: '',
    isActive: 'true',
  });

  const fetchData = useCallback(async () => {
    setQuery((query) => ({ ...query, isFetching: true }));

    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .reports()
        .schedules()
        .list({
          q: params.q ?? undefined,
          isActive: params.isActive ?? undefined,
        });
      setQuery({ data, isReady: true, isFetching: false });
    } catch (error) {
      setQuery({ data: null, isReady: true, isFetching: false });
    }
  }, [workspace.id, api, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = ({ target: { name, value } }) => {
    setQuery((query) => ({ ...query, isFetching: true }));
    setParams((params) => ({ ...params, [name]: value }));
  };

  function handleClose() {
    setDialog(null);
    documentTitle.set('Report Schedules');
  }

  const handleDeleteConfirmation = async (reportSchedule) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        resolve={async (result) => {
          if (!result) return resolve(false);

          try {
            await api.www.workspaces(workspace.id).reports().schedules(reportSchedule.id).delete();
            await handleDeleted();
            resolve(true);
          } catch (error) {
            toast.error(error.message || 'There was a problem deleting the report schedule. Please try again.');
          }
        }}>
        Are you sure you want to delete this report schedule?
      </DeleteConfirmation>
    ));
  };

  async function handleSaved() {
    fetchData();
  }

  async function handleDeleted() {
    fetchData();
    handleClose();
  }

  async function handleActiveStatusChange(reportSchedule, flag) {
    try {
      await api.www.workspaces(workspace.id).reports().schedules(reportSchedule.id).setActiveStatus(flag);
      await fetchData();
    } catch (error) {
      toast.error(error.message || 'There was a problem updating the report schedule status. Please try again.');
    }
  }

  const [dialog, setDialog] = useState(null);

  const handleNew = () => {
    setDialog(<ReportScheduleForm onSaved={handleSaved} onClose={handleClose} />);
  };

  const handleEdit = ({ id }) => {
    setDialog(
      <ReportScheduleForm
        reportScheduleId={id}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        onClose={handleClose}
      />,
    );
  };

  const handleRun = async (reportSchedule) => {
    try {
      await api.www.workspaces(workspace.id).reports().schedules(reportSchedule.id).run();
      toast.success(`Report Schedule has run successfully. Go to Exports to download the file.`);
    } catch (error) {
      toast.error(error.message || 'There was a problem running the report schedule. Please try again.');
    }
  };

  if (!isReady) return <PageLoader />;
  if (!data) return <ErrorPage.NotFound publicSite={false} />;

  return (
    <Page scrollable>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Reports</Page.Eyebrow>
          <Page.Title>Schedules</Page.Title>
        </Page.Info>

        <Page.Actions>
          <IconButton icon="plus" tooltip="New Report Schedule" onClick={handleNew} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput name="q" onChange={handleFilter} placeholder="Search" />

          <SingleSelectFilter
            icon="filter"
            name="isActive"
            value={params.isActive}
            options={[
              { id: 'true', name: 'Active' },
              { id: 'false', name: 'Inactive' },
            ]}
            placeholder="Status"
            onChange={handleFilter}
          />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="2rem" />
            <ListView.Column sticky minWidth="10rem" name="name">
              Report
            </ListView.Column>
            <ListView.Column minWidth="20rem">Description</ListView.Column>
            <ListView.Column minWidth="10rem">Schedule</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body fade={isFetching}>
            {data.map((rs) => {
              async function handleRowClick(reportSchedule) {
                handleEdit(reportSchedule);
              }

              return (
                <ListView.Row isDisabled={!rs.isActive} key={rs.id} onClick={() => handleRowClick(rs)}>
                  <ListView.Cell>
                    <Tooltip
                      message={{ csv: 'CSV format', xlsx: 'Excel format' }[rs.format]}
                      style={{ width: '1.5rem' }}>
                      <Icon
                        color={colors.primary}
                        icon={{ csv: 'file-csv', xlsx: 'file-excel' }[rs.format]}
                        spaceRight
                      />
                    </Tooltip>{' '}
                  </ListView.Cell>
                  <ListView.Cell>{rs.report.name}</ListView.Cell>
                  <ListView.Cell>{rs.report.description}</ListView.Cell>
                  <ListView.Cell>
                    {
                      {
                        daily: 'Daily',
                        weekly: `Weekly on ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][rs.schedule.dayOfWeek - 1]}`,
                        monthly: `Monthly on ${rs.schedule.dayOfMonth === 'last' ? 'the last day of the month' : `day ${rs.schedule.dayOfMonth}`}`,
                      }[rs.schedule.frequency]
                    }{' '}
                    at 8:30 a.m.
                  </ListView.Cell>
                  <ListViewActions>
                    <ListViewActions.Edit onClick={() => handleEdit(rs)} />

                    <hr />

                    <ListViewMenu>
                      {({ setIsOpen }) => {
                        const handleAction = (action) => setIsOpen(false) || action;

                        return (
                          <>
                            <ListViewMenu.Item onClick={() => handleAction(handleEdit(rs))}>Edit</ListViewMenu.Item>

                            <ListViewMenu.Item disabled={!rs.isActive} onClick={() => handleAction(handleRun(rs))}>
                              Run
                            </ListViewMenu.Item>

                            <ListViewMenu.DeleteItem onClick={() => handleAction(handleDeleteConfirmation(rs))}>
                              Delete
                            </ListViewMenu.DeleteItem>

                            <ListViewMenu.Item onClick={() => handleAction(handleActiveStatusChange(rs, !rs.isActive))}>
                              {rs.isActive ? 'Deactivate' : 'Activate'}
                            </ListViewMenu.Item>
                          </>
                        );
                      }}
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Report Schedule" isLoading={isFetching} />
        </ListView>
      </Page.ListView>

      {dialog}
    </Page>
  );
}
